import { useEffect, useMemo, useState } from 'react';
import Button from '../../ui/Button';
import { Dialog } from './AccountBits';
import nurseryService from '../../../services/nursery';
import nurseryAccountsService from '../../../services/nurseryAccounts';
import { errorMessage } from '../../../utils/apiResponse';
import { money, num, round2 } from '../../../utils/nurseryMoney';
import { formatLocalDateInTz } from '../../../utils/timezone';
import { PAYMENT_METHOD_LABELS, type InvoiceRow, type PaymentMethod } from '../../../types/nurseryAccounts';
import type { NurseryCustomer } from '../../../types/nursery';

/**
 * Record a payment by hand, for an organisation with no Xero connected. One
 * receipt is spread across the payer's unpaid invoices, oldest due first
 * unless somebody says otherwise.
 */

type ManualMethod = Exclude<PaymentMethod, 'stripe'>;
const METHODS: ManualMethod[] = ['eft', 'card', 'cash', 'cheque', 'other'];

/** Oldest due first; an invoice with no due date goes by its invoice date. */
const byOldestDue = (a: InvoiceRow, b: InvoiceRow) =>
  (a.due_date || a.invoice_date).localeCompare(b.due_date || b.invoice_date) || a.id - b.id;

/** Spread an amount down the invoices in order, each up to its balance. */
const spread = (amount: number, invoices: InvoiceRow[]): Record<number, string> => {
  let left = round2(amount);
  const out: Record<number, string> = {};
  for (const inv of invoices) {
    const take = round2(Math.max(0, Math.min(left, num(inv.balance))));
    out[inv.id] = take > 0 ? take.toFixed(2) : '';
    left = round2(left - take);
  }
  return out;
};

export interface RecordPaymentDialogProps {
  /** Today in the organisation's timezone, 'YYYY-MM-DD'. */
  today: string;
  timezone: string;
  onClose: () => void;
  /** Called once the payment has been recorded. */
  onDone: () => void;
}

export function RecordPaymentDialog({ today, timezone, onClose, onDone }: RecordPaymentDialogProps) {
  const [payers, setPayers] = useState<NurseryCustomer[]>([]);
  const [payerId, setPayerId] = useState<number | ''>('');
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<ManualMethod>('eft');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');

  const [invoices, setInvoices] = useState<InvoiceRow[] | null>(null);
  // What somebody typed against an invoice. Null while the spread is automatic.
  const [manual, setManual] = useState<Record<number, string> | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    nurseryService.getCustomers({ top_level: true, active: true })
      .then((res) => {
        if (cancelled) return;
        if (res.success) setPayers(res.data || []);
        else setError(errorMessage(res, 'Could not load the accounts'));
      })
      .catch((e) => { if (!cancelled) setError(errorMessage(e, 'Could not load the accounts')); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setInvoices(null);
    setManual(null);
    if (!payerId) return;
    nurseryAccountsService.getInvoices({ payer_id: payerId, state: 'outstanding' })
      .then((res) => {
        if (cancelled) return;
        if (res.success) setInvoices([...(res.data || [])].sort(byOldestDue));
        else {
          setInvoices([]);
          setError(errorMessage(res, 'Could not load the unpaid invoices'));
        }
      })
      .catch((e) => {
        if (cancelled) return;
        setInvoices([]);
        setError(errorMessage(e, 'Could not load the unpaid invoices'));
      });
    return () => { cancelled = true; };
  }, [payerId]);

  const paid = round2(num(amount));
  const alloc = useMemo(
    () => manual ?? spread(paid, invoices ?? []),
    [manual, paid, invoices],
  );

  const allocated = round2((invoices ?? []).reduce((sum, inv) => sum + round2(num(alloc[inv.id])), 0));
  const leftOver = round2(paid - allocated);
  const overInvoice = (invoices ?? []).filter((inv) => round2(num(alloc[inv.id])) > num(inv.balance) + 0.005);
  const negative = (invoices ?? []).some((inv) => num(alloc[inv.id]) < 0);
  const overAmount = allocated > paid + 0.005;

  const ok = !!payerId && paid > 0 && !!date && invoices !== null && overInvoice.length === 0 && !overAmount && !negative;

  const setAllocation = (invoiceId: number, value: string) =>
    setManual({ ...alloc, [invoiceId]: value });

  const submit = async () => {
    if (!payerId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryAccountsService.recordPayment({
        payer_customer_id: payerId,
        payment_date: date,
        amount: paid,
        method,
        ...(reference.trim() ? { reference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        allocations: (invoices ?? [])
          .map((inv) => ({ invoice_id: inv.id, amount: round2(num(alloc[inv.id])) }))
          .filter((a) => a.amount > 0),
      });
      if (res.success) {
        onDone();
        return;
      }
      setError(errorMessage(res, 'Could not record the payment'));
    } catch (e) {
      setError(errorMessage(e, 'Could not record the payment'));
    }
    setBusy(false);
  };

  const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

  return (
    <Dialog
      wide
      title="Record a payment"
      subtitle="One payment from a head office, spread across the invoices it covers."
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={!ok || busy}>{busy ? 'Working…' : 'Record payment'}</Button>
        </>
      }
    >
      {error && <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700 break-words">{error}</div>}

      <div>
        <label htmlFor="payment-payer" className="block text-sm font-medium text-neutral-700 mb-1">Who paid</label>
        <select id="payment-payer" value={payerId} onChange={(e) => setPayerId(e.target.value ? Number(e.target.value) : '')} className={input}>
          <option value="">Choose the account…</option>
          {payers.map((p) => (
            <option key={p.id} value={p.id}>{p.name}{p.customer_code ? ` (#${p.customer_code})` : ''}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label htmlFor="payment-date" className="block text-sm font-medium text-neutral-700 mb-1">Date it was paid</label>
          <input id="payment-date" type="date" value={date} max={today} onChange={(e) => setDate(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="payment-amount" className="block text-sm font-medium text-neutral-700 mb-1">Amount</label>
          <input id="payment-amount" type="number" min="0" step="0.01" inputMode="decimal" value={amount}
            onChange={(e) => setAmount(e.target.value)} className={input} />
        </div>
        <div>
          <label htmlFor="payment-method" className="block text-sm font-medium text-neutral-700 mb-1">How it was paid</label>
          <select id="payment-method" value={method} onChange={(e) => setMethod(e.target.value as ManualMethod)} className={input}>
            {METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="payment-reference" className="block text-sm font-medium text-neutral-700 mb-1">Reference</label>
          <input id="payment-reference" type="text" maxLength={100} value={reference}
            onChange={(e) => setReference(e.target.value)} placeholder="Remittance or bank reference" className={input} />
        </div>
      </div>

      <div>
        <label htmlFor="payment-notes" className="block text-sm font-medium text-neutral-700 mb-1">Notes</label>
        <textarea id="payment-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
      </div>

      {payerId !== '' && (
        <div className="border-t border-neutral-200 pt-3 space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-neutral-800">How it is spread</h3>
            {manual && (
              <button type="button" className="text-sm text-primary-600 hover:underline" onClick={() => setManual(null)}>
                Spread oldest first again
              </button>
            )}
          </div>
          {invoices === null ? (
            <div className="h-16 bg-neutral-100 rounded-lg animate-pulse" />
          ) : invoices.length === 0 ? (
            <p className="text-sm text-neutral-600 break-words">
              This account has no unpaid invoices. The whole payment will sit on the account as credit until there is
              an invoice to set it against.
            </p>
          ) : (
            <>
              <p className="text-xs text-neutral-500 break-words">
                The payment goes to the invoice due longest ago first. Change any figure to match the remittance.
              </p>
              <ul className="space-y-2">
                {invoices.map((inv) => {
                  const over = round2(num(alloc[inv.id])) > num(inv.balance) + 0.005;
                  return (
                    <li key={inv.id} className="border border-neutral-200 rounded-lg p-3">
                      <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
                        <div className="min-w-0 flex-1 basis-40">
                          <label htmlFor={`payment-alloc-${inv.id}`} className="block text-sm font-medium text-neutral-800 break-words">
                            {inv.invoice_number}
                          </label>
                          <p className="text-xs text-neutral-500 break-words">
                            {inv.customer_name}{inv.customer_code ? ` #${inv.customer_code}` : ''}
                            {inv.customer_po ? ` · PO ${inv.customer_po}` : ''}
                          </p>
                          <p className="text-xs text-neutral-500">
                            {inv.due_date ? `Due ${formatLocalDateInTz(inv.due_date.slice(0, 10), timezone)}` : 'No due date'}
                            {' · '}owes {money(inv.balance)}
                          </p>
                        </div>
                        <input id={`payment-alloc-${inv.id}`} type="number" min="0" step="0.01" inputMode="decimal"
                          value={alloc[inv.id] ?? ''} onChange={(e) => setAllocation(inv.id, e.target.value)}
                          className={`${input} sm:w-32 text-right`} />
                      </div>
                      {over && (
                        <p className="text-xs text-red-700 mt-1 break-words" role="alert">
                          That is more than the {money(inv.balance)} this invoice owes.
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </>
          )}

          {invoices !== null && (
            <dl className="bg-neutral-50 rounded-lg px-3 py-2 text-sm space-y-1">
              <div className="flex justify-between gap-3"><dt className="text-neutral-600">Payment</dt><dd>{money(paid)}</dd></div>
              <div className="flex justify-between gap-3"><dt className="text-neutral-600">Set against invoices</dt><dd>{money(allocated)}</dd></div>
              {!overAmount && (
                <div className="flex justify-between gap-3 font-semibold"><dt>Left on account</dt><dd>{money(leftOver)}</dd></div>
              )}
            </dl>
          )}
          {overAmount && (
            <p className="text-sm text-red-700 break-words" role="alert">
              {money(allocated)} is set against invoices, which is {money(round2(allocated - paid))} more than the
              payment of {money(paid)}. Reduce the figures until they add up to the payment or less.
            </p>
          )}
          {negative && (
            <p className="text-sm text-red-700 break-words" role="alert">An amount against an invoice cannot be less than zero.</p>
          )}
        </div>
      )}
    </Dialog>
  );
}

export default RecordPaymentDialog;
